import React, { useState } from 'react';
import { Form, Input, Button, Card, Alert, theme } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LoginRequest } from '../types/auth';
import { useAuth } from '../contexts/AuthContext';
import { isNetworkError } from '../utils/networkUtils';
import LanguageSwitcher from './LanguageSwitcher';

const LoginForm: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { token: { colorBgContainer, borderRadiusLG } } = theme.useToken();

  const onFinish = async (values: LoginRequest) => {
    setIsLoading(true);
    setError(null);
    
    try {
      await login(values);
      // Redirect to homepage after successful login
      navigate('/');
    } catch (err) {
      if (err instanceof Error) {
        // Check if it's a network error
        if (isNetworkError(err)) {
          // Network errors will trigger automatic redirect, but we can show a brief message
          setError(t('auth.login.errors.connection_failed', { 
            defaultValue: 'Unable to connect to server. Please check your connection.' 
          }));
          // The redirect will happen automatically via apiRequest error handling
        } else {
          // Regular authentication error
          setError(t('auth.login.errors.invalid_credentials'));
        }
      } else {
        setError(t('auth.login.errors.invalid_credentials'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
      <div style={{ position: 'absolute', top: 16, right: 16 }}>
        <LanguageSwitcher />
      </div>
      <Card 
        title={<h2 style={{ textAlign: 'center' }}>{t('auth.login.title')}</h2>} 
        style={{ width: 400, boxShadow: '0 4px 8px rgba(0,0,0,0.2)', borderRadius: borderRadiusLG }}
      >
        {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />}
        
        <Form
          name="login"
          initialValues={{ remember: true }}
          onFinish={onFinish}
          layout="vertical"
        >
          <Form.Item
            name="username"
            rules={[{ required: true, message: t('auth.login.errors.username_required') }]}
          >
            <Input 
              prefix={<UserOutlined />} 
              placeholder={t('auth.login.username')} 
              size="large"
            />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[{ required: true, message: t('auth.login.errors.password_required') }]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder={t('auth.login.password')}
              size="large"
            />
          </Form.Item>

          <Form.Item>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={isLoading}
              style={{ width: '100%' }}
              size="large"
            >
              {t('auth.login.submit')}
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default LoginForm; 